/**
 * /reset-password — sets a new password from the token in the reset email.
 *
 * The auth service sends the reset link to the project's `siteUrl`, which is
 * Friday, so the link lands here as `/reset-password?token=…`.
 */

import { useState, type FormEvent } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useAuth } from '@/features/auth/authContext'
import { ApiError } from '@/lib/api'
import { FridayMark } from '@/components/branding/FridayMark'
import { useTheme } from '@/features/theme/ThemeProvider'

const PASSWORD_HINT =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character. Maximum 72.'

function strongEnough(password: string): boolean {
  return (
    password.length >= 8 &&
    // The auth service hashes with bcrypt, which only reads the first 72 bytes.
    password.length <= 72 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /\d/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  )
}

export function ResetPasswordPage() {
  const { resetPassword } = useAuth()
  const search = useSearch({ from: '/reset-password' }) as { token?: string }
  const token = search.token ?? ''
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const { theme } = useTheme()

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (!strongEnough(password)) {
      setError(PASSWORD_HINT)
      return
    }
    setBusy(true)
    try {
      await resetPassword(token, password)
      setDone(true)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reset the password. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-app px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <FridayMark size={64} className="mx-auto mb-3" />
          <h1 className="text-2xl font-semibold tracking-tight">Choose a new password</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {done ? 'Your password has been updated.' : 'Pick something you will actually remember.'}
          </p>
        </div>

        {done ? (
          <div
            className="space-y-4 border border-border bg-elevated p-6 shadow-lg"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <p className="text-sm text-fg-muted">You can sign in with your new password now.</p>
            <Link to="/login" search={{ next: undefined }} className="btn-primary w-full justify-center">
              Sign in
            </Link>
          </div>
        ) : !token ? (
          <div
            className="space-y-4 border border-border bg-elevated p-6 shadow-lg"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <p className="text-sm text-fg-muted">
              This reset link is missing its token. Request a new one and try again.
            </p>
            <Link to="/forgot-password" className="btn-primary w-full justify-center">
              Request a new link
            </Link>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="space-y-3 border border-border bg-elevated p-6 shadow-lg"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <label className="block">
              <span className="text-xs font-medium text-fg">New password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                className="input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <span className="mt-1 block text-xs text-fg-muted">{PASSWORD_HINT}</span>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-fg">Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                className="input mt-1"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={busy}
              />
            </label>
            {error && (
              <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
                {error}
              </div>
            )}
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}

        <p className="mt-4 text-center text-xs text-fg-muted">
          <Link to="/login" search={{ next: undefined }} className="font-medium text-accent hover:underline">
            Back to sign in
          </Link>
          <br />
          <span className="opacity-60">Theme · {theme.name}</span>
        </p>
      </div>
    </div>
  )
}
